// Orchestrates the sign-up chatbot's "continue with Google" popup: prime the
// server-side OAuth state, navigate the already-open popup to Google, then wait
// for the postMessage the OAuth callback page sends back (or for the visitor to
// close the popup themselves). Pure logic — no DOM/window access besides the
// injected `popup` / `win` handles, so this is fully unit-testable without jsdom
// having a real popup.
//
// Navigation itself (submitOAuthForm) DOES touch the DOM: OmniAuth 2.1.3's request
// phase is POST-only (no `allowed_request_methods` override in
// config/initializers/omniauth.rb), so a plain `popup.location.href = oauthUrl` GET
// hits a 404. The legacy signup button works around this with a `button_to` (a POST
// form carrying Rails' CSRF authenticity_token) — this mirrors that: a hidden form,
// targeted at the already-open named popup, submitted and torn down synchronously.
export const SIGNUP_OAUTH_WINDOW_NAME = 'signup-google'

// Posts a hidden form into the popup window named SIGNUP_OAUTH_WINDOW_NAME — the
// same mechanism as the legacy `button_to "/auth/google_oauth2", method: :post`
// button, so OmniAuth's POST-only request phase accepts it (a GET 404s).
export function submitOAuthForm(actionUrl: string, csrfToken: string | null): void {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = actionUrl
  form.target = SIGNUP_OAUTH_WINDOW_NAME
  form.style.display = 'none'
  if (csrfToken !== null) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = 'authenticity_token'
    input.value = csrfToken
    form.appendChild(input)
  }
  document.body.appendChild(form)
  form.submit()
  form.remove()
}

export type OAuthPopupResult =
  | { status: 'success'; redirectUrl: string }
  | { status: 'account_exists'; email: string }
  | { status: 'error'; reason: string }
  | { status: 'cancelled' }

interface OAuthPopupMessage {
  source?: string
  status?: string
  redirectUrl?: string
  email?: string
  reason?: string
}

// The relayed destination is treated as untrusted input even though the popup page is ours:
// only a same-origin URL (relative path or absolute on `origin`) is followed; anything else —
// foreign host, protocol-relative, javascript:, non-string — degrades to the home page.
function sameOriginRedirect(candidate: unknown, origin: string): string {
  if (typeof candidate !== 'string' || candidate === '') return '/'
  try {
    return new URL(candidate, origin).origin === origin ? candidate : '/'
  } catch {
    return '/'
  }
}

export function runGoogleOAuthPopup(
  popup: Window,
  opts: {
    /** Server-side priming call (sets up OAuth state); false means abort before navigating. */
    prime: () => Promise<boolean>
    /** Navigates the already-open popup once priming succeeds (e.g. submitOAuthForm). */
    navigate: () => void
    /** Origin the result postMessage must come from — anything else is ignored. */
    expectedOrigin: string
    /** Window to attach the message listener / poll on — injectable for tests. */
    win?: Window & typeof globalThis
  },
): Promise<OAuthPopupResult> {
  const win = opts.win ?? window
  return new Promise<OAuthPopupResult>((resolve) => {
    let settled = false
    let poll: ReturnType<typeof setInterval> | undefined

    const finish = (result: OAuthPopupResult) => {
      if (settled) return
      settled = true
      win.removeEventListener('message', onMessage)
      if (poll) clearInterval(poll)
      resolve(result)
    }

    const onMessage = (e: MessageEvent) => {
      if (e.origin !== opts.expectedOrigin) return
      const data = e.data as OAuthPopupMessage
      if (!data || data.source !== 'signup-oauth') return
      if (data.status === 'success') {
        finish({ status: 'success', redirectUrl: sameOriginRedirect(data.redirectUrl, opts.expectedOrigin) })
        return
      }
      if (data.status === 'account_exists') {
        finish({ status: 'account_exists', email: data.email ?? '' })
        return
      }
      finish({ status: 'error', reason: data.reason ?? 'unknown' })
    }

    opts
      .prime()
      .then((ok) => {
        if (!ok) {
          try {
            popup.close()
          } catch {
            /* ignore — popup may already be closed/unreachable */
          }
          finish({ status: 'error', reason: 'prime_failed' })
          return
        }
        win.addEventListener('message', onMessage)
        opts.navigate()
        // The OAuth callback page posts a message on success; if the visitor closes
        // the popup instead (no message ever arrives), fall back to `cancelled`.
        poll = setInterval(() => {
          if (popup.closed) finish({ status: 'cancelled' })
        }, 400)
      })
      // A REJECTING prime (network/DNS failure) would otherwise leave the promise
      // unsettled and lock the UI (submitInFlight never clears). Treat it exactly like
      // an `ok === false` prime: close the popup and resolve `prime_failed`.
      .catch(() => {
        try {
          popup.close()
        } catch {
          /* ignore — popup may already be closed/unreachable */
        }
        finish({ status: 'error', reason: 'prime_failed' })
      })
  })
}
